import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

const FLOW_STEPS = [
    { label: 'Send OTP' },
    { label: 'Verify & Get Report' },
];

const AadhaarVerificationOtpInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Step 0 — Send OTP
    const [aadhaarNo, setAadhaarNo] = useState('');
    const [sendConsent, setSendConsent] = useState(false);
    const [sendErrors, setSendErrors] = useState({});
    const [applicationId, setApplicationId] = useState('');
    const [maskedAadhaar, setMaskedAadhaar] = useState('');

    // Step 1 — Submit OTP
    const [otp, setOtp] = useState('');
    const [shareCode, setShareCode] = useState('');
    const [submitConsent, setSubmitConsent] = useState(false);
    const [submitErrors, setSubmitErrors] = useState({});
    const [submitResult, setSubmitResult] = useState(null);

    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${serviceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [serviceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const rd = error.response?.data;
        let detail;
        if (s === 402) {
            detail = `Insufficient credits. Required: ${rd?.required_credits ?? 'N/A'}, Available: ${rd?.current_balance ?? 'N/A'}`;
        } else if (s === 422) {
            const errs = rd?.errors;
            detail = errs ? Object.values(errs)[0]?.[0] : (rd?.message || 'Validation failed');
        } else if (s === 429) {
            detail = 'Too many requests. Please wait a minute before trying again.';
        } else {
            detail = rd?.message || error.message || summary;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    // ── Step 0: Send OTP ────────────────────────────────────────────────────
    const validateSend = () => {
        const next = {};
        if (!aadhaarNo.trim()) next.aadhaarNo = 'Aadhaar number is required';
        else if (!/^\d{12}$/.test(aadhaarNo.trim())) next.aadhaarNo = 'Aadhaar number must be exactly 12 digits';
        if (!sendConsent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSendOtp = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/tartan/aadhaar/request-otp', {
                aadhaar_no: aadhaarNo.trim(),
            });
            if (!res.data.success) throw new Error(res.data.message || 'OTP request failed');
            setApplicationId(res.data.data?.application_id || '');
            setMaskedAadhaar(res.data.data?.masked_aadhaar || aadhaarNo.replace(/\d(?=\d{4})/g, 'X'));
            toast.current?.show({ severity: 'success', summary: 'OTP sent', detail: 'Enter the OTP received on your Aadhaar-linked mobile', life: 3000 });
            setStep(1);
        } catch (err) {
            showError(err, 'OTP request failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSendSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateSend();
        if (Object.keys(errs).length) {
            setSendErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setSendErrors({});
        submittingRef.current = true;
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Aadhaar Number:</span> <span className="text-900 font-medium font-mono">{aadhaarNo.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3')}</span></div>
                    <p className="text-500 mt-2 mb-0">An OTP will be sent to the mobile number registered with this Aadhaar.</p>
                </div>
            ),
            header: 'Confirm OTP request',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, send OTP',
            rejectLabel: 'Cancel',
            accept: doSendOtp,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    // ── Step 1: Submit OTP ──────────────────────────────────────────────────
    const validateSubmit = () => {
        const next = {};
        if (!otp.trim()) next.otp = 'OTP is required';
        else if (!/^\d{6}$/.test(otp.trim())) next.otp = 'OTP must be exactly 6 digits';
        if (!shareCode.trim()) next.shareCode = 'Share code is required';
        else if (shareCode.trim().length !== 4) next.shareCode = 'Share code must be exactly 4 characters';
        if (!submitConsent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSubmitOtp = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/tartan/aadhaar/submit-otp', {
                aadhaar_no: aadhaarNo.trim(),
                otp: otp.trim(),
                share_code: shareCode.trim(),
                api_provider_service_id: parseInt(serviceId, 10),
                application_id: applicationId || undefined,
            });
            if (!res.data.success) throw new Error(res.data.message || 'Verification failed');
            setSubmitResult(res.data);
            toast.current?.show({ severity: 'success', summary: 'Aadhaar verified', detail: 'Verification completed successfully', life: 2500 });
            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (_) {}
        } catch (err) {
            showError(err, 'Verification failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmitOtp = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateSubmit();
        if (Object.keys(errs).length) {
            setSubmitErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setSubmitErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Aadhaar:</span> <span className="text-900 font-medium">{maskedAadhaar}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm Aadhaar verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: doSubmitOtp,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const resetFlow = () => {
        setStep(0);
        setAadhaarNo('');
        setSendConsent(false);
        setSendErrors({});
        setApplicationId('');
        setMaskedAadhaar('');
        setOtp('');
        setShareCode('');
        setSubmitConsent(false);
        setSubmitErrors({});
        setSubmitResult(null);
        submittingRef.current = false;
    };

    // ── Renderers ────────────────────────────────────────────────────────────
    const renderStep0 = () => (
        <Card>
            <form onSubmit={handleSendSubmit} noValidate>
                <div className="grid">
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="aadhaarNo" className="block font-medium text-sm mb-2">
                            Aadhaar Number <span className="text-red-500">*</span>
                        </label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-id-card" />
                            <InputText
                                id="aadhaarNo"
                                value={aadhaarNo}
                                onChange={(e) => {
                                    const v = (e.target.value || '').replace(/\D/g, '');
                                    setAadhaarNo(v);
                                    if (sendErrors.aadhaarNo) setSendErrors((p) => ({ ...p, aadhaarNo: '' }));
                                }}
                                placeholder="12-digit Aadhaar number"
                                maxLength={12}
                                inputMode="numeric"
                                autoComplete="off"
                                disabled={loading}
                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!sendErrors.aadhaarNo })}
                                aria-describedby="aadhaarNo-help"
                                aria-invalid={!!sendErrors.aadhaarNo}
                            />
                        </IconField>
                        {sendErrors.aadhaarNo && (
                            <small id="aadhaarNo-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                {sendErrors.aadhaarNo}
                            </small>
                        )}
                    </div>
                </div>
                <ConsentCheckbox
                    checked={sendConsent}
                    onChange={(e) => { setSendConsent(e.checked); if (sendErrors.consent) setSendErrors((p) => ({ ...p, consent: '' })); }}
                    error={sendErrors.consent}
                    reportType="aadhaar-verification-otp"
                />
                <div className="flex justify-content-end mt-3">
                    <Button type="submit" label="Send OTP" icon="pi pi-send" iconPos="right" loading={loading} disabled={loading} size="small" />
                </div>
            </form>
        </Card>
    );

    const renderStep1 = () => (
        <div>
            {!submitResult ? (
                <Card>
                    {maskedAadhaar && (
                        <div className="mb-3 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
                            <i className="pi pi-id-card text-primary text-sm" />
                            <span className="text-600 text-xs">Aadhaar:</span>
                            <span className="font-mono text-sm text-900 font-medium">{maskedAadhaar}</span>
                        </div>
                    )}
                    <form onSubmit={handleSubmitOtp} noValidate>
                        <div className="grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="aadhaarOtp" className="block font-medium text-sm mb-2">
                                    OTP <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-lock" />
                                    <InputText
                                        id="aadhaarOtp"
                                        value={otp}
                                        onChange={(e) => {
                                            const v = (e.target.value || '').replace(/\D/g, '');
                                            setOtp(v);
                                            if (submitErrors.otp) setSubmitErrors((p) => ({ ...p, otp: '' }));
                                        }}
                                        placeholder="6-digit OTP"
                                        maxLength={6}
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        disabled={loading}
                                        className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!submitErrors.otp })}
                                        aria-describedby="otp-help"
                                        aria-invalid={!!submitErrors.otp}
                                    />
                                </IconField>
                                {submitErrors.otp ? (
                                    <small id="otp-help" className="p-error block mt-1" role="alert">
                                        <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                        {submitErrors.otp}
                                    </small>
                                ) : (
                                    <small className="text-500 block mt-1">OTP sent to Aadhaar-linked mobile</small>
                                )}
                            </div>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="shareCode" className="block font-medium text-sm mb-2">
                                    Share Code <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-key" />
                                    <InputText
                                        id="shareCode"
                                        value={shareCode}
                                        onChange={(e) => {
                                            const v = (e.target.value || '').slice(0, 4);
                                            setShareCode(v);
                                            if (submitErrors.shareCode) setSubmitErrors((p) => ({ ...p, shareCode: '' }));
                                        }}
                                        placeholder="4-character code"
                                        maxLength={4}
                                        disabled={loading}
                                        className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!submitErrors.shareCode })}
                                        aria-describedby="shareCode-help"
                                        aria-invalid={!!submitErrors.shareCode}
                                    />
                                </IconField>
                                {submitErrors.shareCode ? (
                                    <small id="shareCode-help" className="p-error block mt-1" role="alert">
                                        <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                        {submitErrors.shareCode}
                                    </small>
                                ) : (
                                    <small className="text-500 block mt-1">4-character code to protect your Aadhaar XML</small>
                                )}
                            </div>
                        </div>
                        <ConsentCheckbox
                            checked={submitConsent}
                            onChange={(e) => { setSubmitConsent(e.checked); if (submitErrors.consent) setSubmitErrors((p) => ({ ...p, consent: '' })); }}
                            error={submitErrors.consent}
                            reportType="aadhaar-verification-otp-submit"
                        />
                        <div className="flex justify-content-between mt-3">
                            <Button type="button" label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} disabled={loading} />
                            <Button type="submit" label="Verify Aadhaar" icon="pi pi-check" iconPos="right" loading={loading} disabled={loading} size="small" />
                        </div>
                    </form>
                </Card>
            ) : (
                <>
                    <GenericSurepassOutputComponent reportData={submitResult} title="Aadhaar Verified" icon="pi-check-circle" />
                    <div className="flex justify-content-end mt-3">
                        <Button label="Verify Another" icon="pi pi-refresh" outlined size="small" onClick={resetFlow} />
                    </div>
                </>
            )}
        </div>
    );

    const stepRenderers = [renderStep0, renderStep1];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <h1 className="text-2xl font-semibold mb-3">Aadhaar Verification with OTP</h1>
                    <ReportBanner
                        icon="pi-id-card"
                        title="Aadhaar Verification with OTP"
                        subtitle="Verify Aadhaar identity by sending an OTP to the registered mobile number"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default AadhaarVerificationOtpInputComponent;
